import React from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { media } from 'styled-bootstrap-grid';
import { useTranslations } from '@lang';
import Markdown from '@common/components/Markdown';
import { Title4 } from '@common/components/Typography';
import ImgixWrapper from '@common/components/ImgixWrapper';
import { Button } from '@common/components';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.defaultColors.lighterGrey};
  padding: 40px 0;

  &:first-of-type {
    padding-top: 60px;
  }

  &:last-of-type {
    padding-bottom: 100px;
    border-bottom: none;
  }

  ${media.xl`
    flex-direction: ${({ isReversed }) => (isReversed ? 'row-reverse' : 'row')};
    align-items: center;
    margin-left: -30px;
    margin-right: -30px;
    border-bottom: none;
    ${({ isReversed }) =>
      isReversed &&
      css`
        justify-content: space-between;
      `};
  `};
`;

const ImageWrapper = styled.div`
  max-width: 100%;
  ${media.xl`
    padding: 0 30px;
    margin: 0;
  `};

  img {
    max-width: 100%;
    height: auto;
  }
`;

const TextWrapper = styled.div`
  width: 100%;
  margin-top: 10px;
  ${media.xl`
    width: auto;
    margin-top: 0;
    max-width: 570px;
    padding: 0 30px;
    margin: 0;
  `};
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.defaultColors.fontSecondary};
  text-transform: uppercase;
  letter-spacing: 1.12px;
  font-size: 14px;
  margin: 0;
`;

const WrappedText = styled.div`
  font-size: 16px;
  padding: 5px 0;
  line-height: 150%;
  text-align: left;
  color: ${({ theme }) => theme.defaultColors.fontSecondary};
  margin-bottom: 20px;
`;

const StyledImagePlaceholder = styled.div`
  margin: 0;
  background-color: ${({ theme }) => theme.tertiaryColour};
  width: 690px;
  height: 390px;
  max-width: 100%;
`;

const Menus = styled.div`
  padding: 20px 0;
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(3, 1fr);
  ${media.xs`
    grid-template-columns: 1fr;
  `}
`;

const RestaurantBox = ({
  image = null,
  title,
  subtitle = '',
  description = '',
  isReversed = false,
  menu = [],
  handleAddClick,
}) => {
  const { t } = useTranslations();

  return (
    <Wrapper isReversed={isReversed}>
      <ImageWrapper>
        {image ? (
          <ImgixWrapper src={image} width={690} height={390} sizes="auto" />
        ) : (
          <StyledImagePlaceholder />
        )}
      </ImageWrapper>
      <TextWrapper>
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
        <Title4 tag="h3">{title}</Title4>
        {description && (
          <div>
            <WrappedText>
              <Markdown text={description} />
            </WrappedText>
          </div>
        )}
        <Button isPrimary onClick={handleAddClick}>
          {t('tables.bookTable')}
        </Button>
        <Menus>
          {menu.map(({ name, file }) => (
            <Button
              key={file.externalKey}
              tagName="a"
              href={file.url}
              download
              rel="noopener noreferrer"
              target="_blank"
              isSmall
            >
              {name}
            </Button>
          ))}
        </Menus>
      </TextWrapper>
    </Wrapper>
  );
};

RestaurantBox.propTypes = {
  image: PropTypes.string,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  description: PropTypes.string,
  isReversed: PropTypes.bool,
  menu: PropTypes.array,
};

export default RestaurantBox;
